import React, { useState, useEffect, useCallback, useLayoutEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './RowContextMenu.css';

const CLOSED_MENU = { open: false, x: 0, y: 0, row: null };

/**
 * Manages open/close/position state for a table row right-click menu.
 * Pass `handleRowContextMenu` to Table's `onRowContextMenu`.
 */
export const useRowContextMenu = () => {
  const [menu, setMenu] = useState(CLOSED_MENU);

  const close = useCallback(() => {
    setMenu(prev => (prev.open ? CLOSED_MENU : prev));
  }, []);

  const handleRowContextMenu = useCallback((row, index, event) => {
    if (!row) return;
    event.preventDefault();
    setMenu({ open: true, x: event.clientX, y: event.clientY, row });
  }, []);

  useEffect(() => {
    if (!menu.open) return undefined;
    const handleKey = (event) => {
      if (event.key === 'Escape') {
        close();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [menu.open, close]);

  return { menu, close, handleRowContextMenu };
};

/**
 * Overlay + menu for table row actions. Hide items with `hidden`; mark destructive ones with `danger`.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {number} props.x
 * @param {number} props.y
 * @param {Function} props.onClose
 * @param {Array<{ key: string, icon: object, label: string, onClick: Function, hidden?: boolean, danger?: boolean, disabled?: boolean }>} props.items
 */
const RowContextMenu = ({ open, x, y, onClose, items = [] }) => {
  const menuRef = useRef(null);
  const visibleItems = items.filter(item => !item.hidden);

  useLayoutEffect(() => {
    if (!open || !menuRef.current) return;
    const el = menuRef.current;
    const rect = el.getBoundingClientRect();
    let top = y;
    let left = x;
    if (rect.bottom > window.innerHeight) {
      top = Math.max(8, window.innerHeight - rect.height - 8);
    }
    if (rect.right > window.innerWidth) {
      left = Math.max(8, window.innerWidth - rect.width - 8);
    }
    el.style.top = `${top}px`;
    el.style.left = `${left}px`;
  }, [open, x, y, visibleItems.length]);

  if (!open || visibleItems.length === 0) {
    return null;
  }

  return (
    <div
      className="row-context-menu-overlay"
      onClick={onClose}
      onContextMenu={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      <div
        ref={menuRef}
        className="row-context-menu"
        style={{ top: y, left: x }}
        onClick={(event) => event.stopPropagation()}
      >
        {visibleItems.map(item => (
          <button
            key={item.key}
            type="button"
            className={`row-context-menu-item${item.danger ? ' row-context-menu-item-danger' : ''}`}
            disabled={item.disabled}
            onClick={() => {
              onClose();
              if (!item.disabled) {
                item.onClick();
              }
            }}
          >
            {item.icon && <FontAwesomeIcon icon={item.icon} fixedWidth />}
            {item.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default RowContextMenu;
